'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button } from '#pplx/frontend/aether/components/Button/index.js';
import { HeadingPrimitive } from '#pplx/frontend/aether/primitives/HeadingPrimitive/index.js';
import { PplxError } from '#pplx/frontend/common/errors.js';
import { logger } from '#pplx/frontend/logger/browser.js';
import { ReactRenderingError } from '#pplx/frontend/platform-components/errors.js';
import { reloadWindow } from '#pplx/frontend/platform-components/reloadWindow.js';
import React, { useCallback } from 'react';
import { ErrorBoundary as ReactErrorBoundary } from 'react-error-boundary';
import { useIntl } from 'react-intl';
const reset = (details) => {
    if (details.reason === 'imperative-api')
        reloadWindow('Error boundary');
};
const getDefaultCode = (isNullFallback) => isNullFallback ? 'NULL_REACT_RENDERING_ERROR' : 'REACT_RENDERING_ERROR';
const Fallback = ({ resetErrorBoundary }) => {
    const { $t } = useIntl();
    const handleClick = useCallback(() => {
        resetErrorBoundary();
    }, [resetErrorBoundary]);
    return (_jsx("div", { className: "bg-base overflow-hidden rounded-xl", children: _jsxs("div", { className: "bg-subtle px-4 flex w-full items-center justify-between py-3", children: [_jsx(HeadingPrimitive, { level: 3, size: "small", children: $t({
                        defaultMessage: 'Something went wrong.',
                        id: '8c141c7545',
                        description: 'Text in the Error Boundary shared UI section',
                    }) }), _jsx(Button, { size: "small", onClick: handleClick, children: $t({
                        defaultMessage: 'Retry',
                        id: 'd0c1659fe4',
                        description: 'Action button label in the Error Boundary shared UI section',
                    }) })] }) }));
};
const NullFallback = React.memo(function NullFallback() {
    return null;
});
export const ErrorBoundary = React.memo(function ErrorBoundary({ fallback = Fallback, fallbackRender, children, onError, resetKeys, code = getDefaultCode(fallback === null), }) {
    const errorHandler = useCallback((err, errorInfo) => {
        onError?.(err, errorInfo);
        // From: https://docs.datadoghq.com/real_user_monitoring/browser/collecting_browser_errors/?tab=npm#react-error-boundaries-instrumentation
        const renderingError = err instanceof PplxError
            ? err
            : new ReactRenderingError(code, {
                message: err.message,
                cause: err,
            });
        renderingError.stack = errorInfo.componentStack || undefined;
        logger.error(renderingError);
    }, [onError, code]);
    const fallbackProps = fallbackRender
        ? { fallbackRender }
        : { FallbackComponent: fallback || NullFallback };
    return (_jsx(ReactErrorBoundary, { ...fallbackProps, onError: errorHandler, onReset: reset, resetKeys: resetKeys, children: children }));
});
//# sourceMappingURL=ErrorBoundary.js.map